import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { AuthorizationButton } from './AuthorizationButton.jsx';
import { useGoogleLogin } from '@react-oauth/google';
import {
  useAuthorizeGoogleCalendarMutation,
  useGetTaxProAuthInfoQuery,
  useGetTaxProsQuery,
  useDeleteTaxProMutation
} from '../../../../api/taxProConsultationApi.js';
import { useImportUserDataMutation } from '../../../../api/userApi.js';
import { setSelectedUserId } from '../../../../actions/userActions.js';
import { Loader } from '../../../Common/index.js';
import { useDispatch, useSelector } from 'react-redux';
import { REACT_APP_ENV } from '../../../../global/Environment.js';
import _ from 'lodash';
import { isAdminSelector, isInternalSelector, isTaxProSelector } from '../../../../selectors/authSelectors.js';

/** Modal containing web ops settings - for now, this is just tax pro auth mechanics (zoom and google oauth consent) */
const SettingsModal = ({ isOpen, setIsOpen }) => {
  const isAdmin = useSelector(isAdminSelector);
  const isInternal = useSelector(isInternalSelector);
  const isTaxPro = useSelector(isTaxProSelector);

  const { data: taxProAuthInfo, isLoading: isTaxProAuthInfoLoading } = useGetTaxProAuthInfoQuery();
  const { data: taxPros, isLoading: isTaxProsLoading } = useGetTaxProsQuery();

  const { doesNeedGoogleAuthorization, doesNeedZoomAuthorization, googleAuthInformation, zoomAuthInformation } =
    _.defaultTo(taxProAuthInfo, {});

  const authorizeZoom = () => {
    window.location.href = zoomAuthInformation.redirectUri;
  };

  useEffect(() => {
    if ((doesNeedGoogleAuthorization && isAdmin) || (doesNeedZoomAuthorization && isTaxPro)) {
      setIsOpen(true);
    }
  }, [doesNeedGoogleAuthorization, doesNeedZoomAuthorization, isAdmin, isTaxPro, setIsOpen]);

  const isLoading = isTaxProAuthInfoLoading || isTaxProsLoading;

  return (
    <>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='h5'>Settings</Typography>
            <IconButton edge='end' color='inherit' onClick={() => setIsOpen(false)} aria-label='close'>
              <FontAwesomeIcon icon={['fa', 'times']} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Stack
              sx={{
                justifyContent: 'center',
                p: 2.5
              }}
            >
              <Loader />
            </Stack>
          ) : (
            <>
              {(isTaxPro || isAdmin) && (
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 'bold',
                    mb: 1
                  }}
                >
                  Authorizations
                </Typography>
              )}
              {isTaxPro && (
                <Box
                  sx={{
                    mb: 2
                  }}
                >
                  <AuthorizationButton
                    doesNeedAuthorization={doesNeedZoomAuthorization}
                    applicationName='Zoom'
                    onClick={authorizeZoom}
                  />
                </Box>
              )}
              {isAdmin && (
                <>
                  <Box
                    sx={{
                      mb: 2
                    }}
                  >
                    <Typography
                      variant='body1'
                      color='red'
                      sx={{
                        fontWeight: 'bold',
                        mb: 1
                      }}
                    >
                      {REACT_APP_ENV === 'development'
                        ? 'Authorize the account that you want to schedule tax pro calls locally. In production, this will probably be noreply@keepertax.com, but feel free to use any keepertax domain email in development'
                        : 'Warning! Authorizing an account will cause all tax pro calls to be scheduled from that email. You should only authorize the email that will be used to schedule calls (probably noreply@keepertax.com). DO NOT AUTHORIZE YOUR OWN EMAIL'}
                    </Typography>
                    <Box
                      sx={{
                        mb: 1
                      }}
                    >
                      <GoogleAuthorizationButton
                        googleCalendarAuthInformation={googleAuthInformation}
                        doesNeedAuthorization={doesNeedGoogleAuthorization}
                        applicationName='tax pro meeting scheduler Google'
                      />
                    </Box>
                  </Box>
                  <TaxProManagement taxPros={taxPros} />
                </>
              )}
              {isInternal && REACT_APP_ENV !== 'production' && (
                <ImportUserData onCloseSettings={() => setIsOpen(false)} />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const TaxProManagement = ({ taxPros }) => {
  const [deleteTaxPro, { isLoading: isDeletingTaxPro }] = useDeleteTaxProMutation();
  const [taxProPendingRemoval, setTaxProPendingRemoval] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState('');

  const removeTaxPro = async () => {
    if (taxProPendingRemoval) {
      try {
        const response = await deleteTaxPro({ taxProId: taxProPendingRemoval.id }).unwrap();
        const csvUrl = _.get(response, 'csvUrl');
        window.open(csvUrl, '_blank');
        setNotificationMessage('Tax pro removed successfully. CSV downloaded.');
      } catch (error) {
        setNotificationMessage('Failed to remove tax pro. Please try again.');
      }
    }
    setTaxProPendingRemoval(null);
  };
  return (
    <Box
      sx={{
        mb: 2,
        mt: 2
      }}
    >
      <Typography
        variant='subtitle2'
        sx={{
          fontWeight: 'bold',
          mb: 2
        }}
      >
        Tax Pro Management
      </Typography>
      <Stack spacing={2}>
        {_.map(taxPros, (taxPro) => (
          <Stack
            key={taxPro.name}
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='body2'>{taxPro.name}</Typography>
            <Button
              variant='text'
              onClick={() => setTaxProPendingRemoval(taxPro)}
              disabled={isDeletingTaxPro}
              sx={{
                color: 'red',
                fontWeight: 'bold',
                padding: 0,
                minWidth: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              REMOVE
            </Button>
          </Stack>
        ))}
      </Stack>
      <Dialog
        open={!_.isEmpty(taxProPendingRemoval)}
        onClose={() => setTaxProPendingRemoval(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='subtitle1'>
              {taxProPendingRemoval
                ? `Are you sure you want to remove ${taxProPendingRemoval.name}? This can't be undone`
                : ''}
            </Typography>
            <IconButton edge='end' color='inherit' onClick={() => setTaxProPendingRemoval(null)} aria-label='close'>
              <FontAwesomeIcon icon={['fa', 'times']} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isDeletingTaxPro ? (
            <Stack
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                p: 2.5
              }}
            >
              <Loader />
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
                mb: 1
              }}
            >
              <Button
                variant='contained'
                onClick={removeTaxPro}
                sx={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#c82333'
                  }
                }}
              >
                YES, REMOVE
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={!!notificationMessage}
        autoHideDuration={6000}
        onClose={() => setNotificationMessage('')}
        message={notificationMessage}
      />
    </Box>
  );
};

const GoogleAuthorizationButton = ({
  googleCalendarAuthInformation,
  doesNeedAuthorization,
  applicationName = 'Google'
}) => {
  const [authorizeGoogleCalendar] = useAuthorizeGoogleCalendarMutation();
  const loginWithCalendarAccess = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/calendar',
    access_type: 'offline',
    prompt: 'consent',
    redirect_uri: googleCalendarAuthInformation.redirectUri,
    state: googleCalendarAuthInformation.state,
    onSuccess: ({ code, state }) => {
      authorizeGoogleCalendar({ code, state });
    }
  });

  return (
    <AuthorizationButton
      doesNeedAuthorization={doesNeedAuthorization}
      applicationName={applicationName}
      onClick={loginWithCalendarAccess}
    />
  );
};

const ImportUserData = ({ onCloseSettings }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [importUserData, { isLoading: isImporting }] = useImportUserDataMutation();

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Import user data?',
      text: 'This will create a new user from the uploaded file.',
      showCancelButton: true,
      confirmButtonText: 'Import',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) {
          container.style.zIndex = '1400';
        }
      }
    });
    if (!confirmation.isConfirmed) {
      return;
    }
    onCloseSettings();
    let payload;
    try {
      const text = await file.text();
      payload = JSON.parse(text);
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid file',
        text: 'The selected file is not valid JSON.'
      });
      return;
    }
    Swal.fire({
      title: 'Importing user data...',
      text: 'This may take a minute. Please don’t close the tab.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    try {
      const result = await importUserData(payload).unwrap();
      const { newUserId } = result;
      await Swal.fire({
        icon: 'success',
        title: 'Import complete',
        text: `New user ${newUserId} created. Redirecting...`,
        timer: 1500,
        showConfirmButton: false
      });
      if (newUserId) {
        dispatch(setSelectedUserId(newUserId));
      }
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Import failed',
        text: 'Could not import user data. Please check the file format and try again.'
      });
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        mt: 2
      }}
    >
      <Typography
        variant='subtitle2'
        sx={{
          fontWeight: 'bold',
          mb: 2
        }}
      >
        Import User Data
      </Typography>
      <Typography
        variant='body2'
        sx={{
          mb: 1
        }}
      >
        Upload a JSON export to create a new user.
      </Typography>
      <Button
        variant='contained'
        color='warning'
        startIcon={<FileUploadIcon />}
        onClick={handleClick}
        disabled={isImporting}
      >
        {isImporting ? 'Importing...' : 'Import user data'}
      </Button>
      <input
        type='file'
        accept='application/json,.json'
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default SettingsModal;
